import type { StorageFacilityListApiResponse } from '@navio/nsdk';
import { URLKeys } from 'noddi-async';
import { type FilterOptionSource, filterUrlState, useFilterSelectionValues, usePagination } from 'noddi-ui-web';
import { storageFacilityLabel, useDebounceValue } from 'noddi-util';
import { toFilterOptionSource, useFilterOptionsGet } from '../useFilterOptionsGet';

/** Title and visible options of the storage facility filter, for the builder next to its options. */
export const STORAGE_FACILITY_FILTER_TEXT = { title: 'Storage facilities', defaultVisibleOptions: 3 };

/**
 * Storage facilities as filter options, searchable. For `unit.multiSelect`. `heldBy`: the param the control
 * writes; an archived facility it holds (picked or excluded, e.g. from a shared link) stays among the options.
 */
export const useStorageFacilityOptions = ({
  serviceDepartmentIds,
  serviceOrganizationIds,
  heldBy
}: {
  serviceDepartmentIds?: number[];
  serviceOrganizationIds?: number[];
  heldBy?: string;
} = {}): FilterOptionSource<number> => {
  const { pageIndex, pageSize, search, setSearch } = usePagination();
  const debouncedSearch = useDebounceValue({ value: search, delay: 300 });
  const includeArchivedIds = useHeldIds(heldBy);

  const query = useFilterOptionsGet({
    type: URLKeys.getPaginatedStorageFacilities,
    input: {
      pageIndex,
      pageSize,
      search: debouncedSearch,
      serviceDepartmentIds,
      serviceOrganizationIds,
      includeArchivedIds
    },
    queryConfig: { select: toStorageFacilityOptions }
  });

  return toFilterOptionSource(query, 'number', { search, onSearch: setSearch });
};

/**
 * Ids a builder's multi-select over `param` holds in the URL, picked or excluded. Read by param name because a
 * source is built before its control: the control cannot pass its own selection into its options request.
 */
const useHeldIds = (param: string | undefined) => {
  // Hooks run either way; without a param nothing is held.
  const { include, exclude } = useFilterSelectionValues(param ?? 'storageFacilityIds', filterUrlState.parseNumber);
  const held = [...include, ...exclude];
  return param && held.length > 0 ? held : undefined;
};

const toStorageFacilityOptions = ({ results }: StorageFacilityListApiResponse) =>
  results.map((facility) => ({
    value: facility.id,
    label: storageFacilityLabel(facility)
  }));
